import React, { useState, useEffect } from 'react';
import { Loader } from 'components/Base';
import Store from 'store/store';
import { compose } from 'ramda';
import api from 'api';
import {
  QuestionType as ExperienceQuestionType,
  ResponseType as ExperienceResponseType
} from 'store/modules/Experience/types';
import withMeta from 'hoc/withMeta';
import QuestionaireForm from 'components/CustomerQuestionnaire/QuestionaireForm';
import { errHelper } from 'utils';
import { Button } from 'components/Base';
import { makeStyles } from '@material-ui/core';
import classnames from 'classnames';

interface ExperienceQuestionnaireProps {
  experienceQuestions: Array<ExperienceQuestionType>;
  experienceResponses: Array<ExperienceResponseType>;
  setExperienceQuestions: any;
  setExperienceResponses: any;
  customer: number;
  portfolio: number;
  onSuccess: Function;
  form?: string;
  customerName?: string;
  complete?: Function;
  showReviewButtons: boolean;
  getResponses: boolean;
  getQuestions?: Function | null;
  submitQuestions?: Function | null;
  showBlankReportButton?: boolean;
}

interface QuestionaireValues {
  [key: string]: string;
}

const { connect, selectors, actions } = Store;

const useStyles = makeStyles({
  blankFormButton: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'flex-end'
  }
});

const ExperienceQuestionnaire = ({
  experienceQuestions,
  experienceResponses,
  setExperienceQuestions,
  setExperienceResponses,
  customer,
  portfolio,
  onSuccess,
  customerName,
  form = 'questionaire',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  complete = () => {},
  showReviewButtons = true,
  showBlankReportButton = true,
  getResponses = true,
  getQuestions = null,
  submitQuestions = null
}: ExperienceQuestionnaireProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBlank, setIsLoadingBlank] = useState(false);
  const [surveyData, setSurveyData] = useState(false);
  const [selections, setSelections] = useState([] as any);

  const questionData = [
    {
      responses: [
        { id: 1, text: 'I have no or very little knowledge about investing.' },
        { id: 2, text: 'I have basic to moderate knowledge about investing.' },
        { id: 3, text: 'I am very knowledgable about investing.' }
      ],
      multiselect: false
    },
    {
      responses: [
        { id: 1, text: 'Managed Funds' },
        { id: 2, text: 'Shares' },
        { id: 3, text: 'Bonds' },
        { id: 4, text: 'Bank Deposits' }
      ],
      multiselect: true
    },
    {
      responses: [
        { id: 1, text: 'Yes' },
        { id: 2, text: 'No' }
      ],
      multiselect: false
    },
    {
      responses: [
        { id: 1, text: 'I have never experienced a financial downturn.' },
        { id: 4, text: 'I was nervous and sold shares.' },
        {
          id: 2,
          text: "No impact, I didn't make any changes to my investment plan."
        },
        { id: 3, text: 'I saw an opportunity and bought more.' }
      ],
      multiselect: false
    }
  ];

  useEffect(() => {
    setIsLoading(true);
    const tempSurveyData = [] as any;

    ((getQuestions && getQuestions()) || api.getExperienceQuestions()).then(
      ({ data }: { data: any }) => {
        let i = 0;
        setExperienceQuestions(data);
        data.forEach((question: any) => {
          const joiner = ' If yes, ';
          const questionParts = question.name.split(joiner);
          tempSurveyData.push({
            id: question.value,
            text: `${questionParts[0]}`,
            responses: questionData[i].responses,
            multiselect: questionData[i].multiselect,
            label: questionParts[1]
              ? questionParts[1].charAt(0).toUpperCase() +
                questionParts[1].slice(1)
              : '',
            joiner
          });
          i++;
        });
        setSurveyData(tempSurveyData);
        if (getResponses) {
          api.getExperienceResponses({ portfolio }).then(({ data }: any) => {
            setExperienceResponses(
              data.results.filter(
                (result: any) => Number(result.customer) === Number(customer)
              )
            );
            setIsLoading(false);
          });
        } else {
          setIsLoading(false);
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    const data = [] as any;
    experienceQuestions
      .map(question => question.value)
      .forEach(id => {
        const response = selections.find(
          (selection: any) => selection.question_id == id
        );
        response.response = [response.response_id].flat();
        response.case = portfolio;
        response.customer = customer;
        data.push(response);
      });

    (
      (submitQuestions && submitQuestions(data)) ||
      api.submitExperienceResponses({
        portfolio,
        responses: { responses: data }
      })
    )
      .then(() => {
        onSuccess();
      })
      .catch((err: any) => {
        errHelper(err);
      });
  };

  const userResponses = experienceResponses.map(response => {
    return {
      response_id:
        response.response.length === 1
          ? response.response[0]
          : response.response,
      ...response,
      question_id: response.question_id.toString()
    };
  });

  const classes = useStyles();

  const downloadQuestionnare = () => {
    setIsLoadingBlank(true);

    return api
      .blankQuestionnaire('exp')
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Experience Questionnaire.pdf`);
        document.body.appendChild(link);
        link.click();
        setIsLoadingBlank(false);
      })
      .catch((err: any) => {
        setIsLoadingBlank(false);
        errHelper(err);
      });
  };

  return (
    <Loader isLoading={isLoading}>
      {showBlankReportButton && (
        <div className={classnames(classes.blankFormButton)}>
          <Button
            variant="contained"
            color="secondary"
            isLoading={isLoadingBlank}
            onClick={() => downloadQuestionnare()}
          >
            Print Blank Form
          </Button>
        </div>
      )}
      <QuestionaireForm
        form={form}
        onSubmit={submit}
        surveyData={surveyData}
        submitted={false}
        isSubmitting={false}
        successful={false}
        title="Experience Questionnaire"
        userResponses={userResponses}
        customerId={customer}
        customerName={customerName}
        complete={complete}
        showReviewButtons={showReviewButtons}
        updateSelections={setSelections}
      >
        <p>
          Part of the process of designing investment portfolios is for us to
          have a good understanding of your general knowledge and experience
          with investing. Please provide the following information to assist us
          in this regard.
        </p>
      </QuestionaireForm>
    </Loader>
  );
};

export default compose(
  connect(
    (state: any) => ({
      experienceQuestions: selectors.experienceSelectors.getQuestions(state),
      experienceResponses: selectors.experienceSelectors.getResponses(state)
    }),
    {
      setExperienceQuestions: actions.experienceActions.setQuestions,
      setExperienceResponses: actions.experienceActions.setResponses
    }
  ),
  withMeta
)(ExperienceQuestionnaire);
